import moment from 'moment';
import {getLocaleContent} from './locale-content.js';

/**
 * Helper functions cho booking form
 */

/**
 * Convert ID sang string để khớp với form value
 */
export const toStringId = id => (id ? String(id) : '');

/**
 * Map tên thành ID từ danh sách
 */
export const mapNameToId = (name, list, nameKey, idKey) => {
    if (!name || !list || !Array.isArray(list)) return '';
    const found = list.find(item => item[nameKey] === name);
    return found?.[idKey] ? String(found[idKey]) : '';
};

/**
 * Validate booking form - Kiểm tra tính hợp lệ của form đặt lịch
 * @param {Object} form - Dữ liệu form
 * @param {string} locale - Ngôn ngữ
 * @returns {Object} - { errors: {}, isValid: boolean }
 */
export const validateBookingForm = (form, locale) => {
    const errors = {};

    if (!form.name?.trim()) {
        errors.name = getLocaleContent(locale, 'page.booking-form.validation.full-name-required');
    }

    if (!form.phone?.trim()) {
        errors.phone = getLocaleContent(locale, 'page.booking-form.validation.phone-required');
    } else if (!/^[0-9]{10,11}$/.test(form.phone.trim())) {
        errors.phone = getLocaleContent(locale, 'page.booking-form.validation.phone-matches');
    }

    if (!form.hospital) {
        errors.hospital = getLocaleContent(locale, 'page.booking-form.validation.hospital-required');
    }

    if (!form.time) {
        errors.time = getLocaleContent(locale, 'page.booking-form.validation.time-required');
    } else {
        const m = moment(form.time);
        if (!m.isValid()) {
            errors.time = getLocaleContent(locale, 'page.booking-form.validation.time-matches');
        } else if (m.isBefore(moment())) {
            errors.time = getLocaleContent(locale, 'page.booking-form.validation.time-past');
        }
    }

    return {
        errors,
        isValid: Object.keys(errors).length === 0,
    };
};

/**
 * Tạo booking signature để detect thay đổi
 */
export const createBookingSignature = bookingData => {
    return JSON.stringify({
        business_id: bookingData.business_id,
        department_id: bookingData.department_id,
        doctor: bookingData.doctor,
        user_id: bookingData.user_id,
        service: bookingData.service,
    });
};

/**
 * Map booking data từ context sang form values
 * @param {Object} bookingData - Booking data từ context
 * @param {Object} masterData - { businessData, departments, userOptions }
 * @param {boolean} clearHospitalOnService - Có clear hospital khi từ service không
 * @returns {Object} - Form values
 */
export const mapBookingDataToForm = (bookingData, masterData, clearHospitalOnService = false) => {
    const {businessData = [], departments = [], userOptions = []} = masterData;

    // Xác định loại booking
    const isFromDoctor = !!(bookingData.doctor || bookingData.user_id);
    const isFromService = !!(bookingData.department_id && !bookingData.doctor && !bookingData.user_id);

    // Map IDs: ưu tiên ID trực tiếp
    const hospitalId = bookingData.business_id
        ? toStringId(bookingData.business_id)
        : mapNameToId(bookingData.hospital, businessData, 'business_name', 'business_id');

    const departmentId = bookingData.department_id
        ? toStringId(bookingData.department_id)
        : mapNameToId(bookingData.department, departments, 'department_name', 'department_id');

    // Ưu tiên user_id trực tiếp, nếu không có thì map từ tên
    const doctorId = bookingData.user_id
        ? toStringId(bookingData.user_id)
        : mapNameToId(bookingData.doctor, userOptions, 'full_name', 'user_id');

    // Tạo form values theo loại booking
    const formValues = {};

    if (isFromService) {
        // Từ dịch vụ: clear doctor, set department
        formValues.doctor = '';
        if (departmentId) formValues.department = departmentId;

        if (clearHospitalOnService) {
            // Trang specialty: clear hospital
            formValues.hospital = '';
        } else {
            // Trang medical facility: set hospital nếu có business_id
            // Nếu đã có hospital trong form rồi thì giữ nguyên, nếu chưa có thì set từ business_id
            if (hospitalId) formValues.hospital = hospitalId;
        }
    } else if (isFromDoctor) {
        // Từ bác sĩ: set tất cả
        if (hospitalId) formValues.hospital = hospitalId;
        if (departmentId) formValues.department = departmentId;
        if (doctorId) formValues.doctor = doctorId;
    } else {
        // Fallback: set các trường có sẵn
        if (hospitalId) formValues.hospital = hospitalId;
        if (departmentId) formValues.department = departmentId;
        if (doctorId) formValues.doctor = doctorId;
    }

    // Set symptoms nếu có
    if (bookingData.service) {
        formValues.symptoms = bookingData.service;
    }

    return formValues;
};

/**
 * Format phone number (chỉ cho phép số, tối đa 11 ký tự)
 */
export const formatPhoneInput = value => {
    return value.replace(/[^0-9]/g, '').slice(0, 11);
};

/**
 * Kiểm tra xem booking data có dữ liệu có ý nghĩa không
 */
export const hasBookingData = bookingData => {
    return !!(
        bookingData.business_id ||
        bookingData.department_id ||
        bookingData.doctor ||
        bookingData.user_id ||
        bookingData.service
    );
};
